import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, X } from "lucide-react";
import { RELEASES, unreadReleases } from "../lib/releases";
import { getPref, setPref } from "../lib/userPrefs";

export const SEEN_KEY = "trat_seen_release";

function formatDate(iso, lang) {
  return new Date(iso).toLocaleDateString(lang === "sv" ? "sv-SE" : "en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// Knappen "Nytt" i lärarvyns topprad. Pricken visar olästa nyheter; panelen
// öppnas bara när läraren klickar. En nyhet märkt highlight visas dessutom en
// gång som en liten ruta i hörnet — men aldrig medan introduktionen pågår.
function WhatsNew({ user, lang, G, suppressTeaser = false, releases = RELEASES }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => getPref(user, SEEN_KEY));
  const [teaserClosed, setTeaserClosed] = useState(false);
  // Vad som var oläst när panelen öppnades — "Ny"-märket ska synas fast
  // allt markeras som läst i samma stund.
  const [newIds, setNewIds] = useState([]);
  const ref = useRef(null);
  const sv = lang === "sv";

  useEffect(() => { setLastSeen(getPref(user, SEEN_KEY)); }, [user]);

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (releases.length === 0) return null;
  const unread = unreadReleases(releases, lastSeen);
  const teaser = !suppressTeaser && !teaserClosed && !open ? unread.find(r => r.highlight) : null;

  function markAllRead() {
    const newest = releases[0].id;
    if (lastSeen === newest) return;
    setLastSeen(newest);
    setPref(user, SEEN_KEY, newest);
  }

  function openPanel() {
    setNewIds(unread.map(r => r.id));
    markAllRead();
    setOpen(true);
  }

  function toggle() {
    if (open) setOpen(false); else openPanel();
  }

  function go(link) {
    setOpen(false);
    setTeaserClosed(true);
    markAllRead();
    if (link) navigate(link);
  }

  const text = (v) => (typeof v === "string" ? v : v?.[lang] || v?.sv || "");

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={unread.length > 0 ? (sv ? `Nytt i tRAT, ${unread.length} olästa` : `What's new, ${unread.length} unread`) : (sv ? "Nytt i tRAT" : "What's new")}
        style={{ position: "relative", display: "flex", alignItems: "center", gap: 6, background: "none", border: `1px solid ${open ? G.gold : G.border}`, borderRadius: 8, color: G.muted, padding: "6px 10px", cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}
      >
        <Megaphone size={14} aria-hidden="true" />
        <span className="whatsnew-label">{sv ? "Nytt" : "New"}</span>
        {unread.length > 0 && (
          <span aria-hidden="true" style={{ position: "absolute", top: -3, right: -3, width: 9, height: 9, borderRadius: "50%", background: G.gold, border: `2px solid ${G.surface}` }} />
        )}
      </button>

      {open && (
        <div role="dialog" aria-label={sv ? "Nytt i tRAT" : "What's new"} style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: "min(360px, calc(100vw - 32px))", maxHeight: "min(480px, 70vh)", overflowY: "auto", background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.25)", zIndex: 200 }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${G.border}`, fontSize: 14, fontWeight: 600, color: G.text }}>
            {sv ? "Nytt i tRAT" : "What's new"}
          </div>
          {releases.map((r, i) => {
            const isNew = newIds.includes(r.id);
            return (
              <div key={r.id} style={{ padding: "12px 16px", borderTop: i > 0 ? `1px solid ${G.border}` : "none" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: G.muted, fontFamily: "'DM Mono', monospace" }}>{formatDate(r.date, lang)}</span>
                  {isNew && <span style={{ fontSize: 10, fontWeight: 600, color: G.gold, background: G.gold + "18", borderRadius: 4, padding: "1px 6px" }}>{sv ? "Ny" : "New"}</span>}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: G.text, marginBottom: 4 }}>{text(r.title)}</div>
                <div style={{ fontSize: 13, color: G.sub, lineHeight: 1.55 }}>{text(r.body)}</div>
                {r.link && (
                  <button onClick={() => go(r.link)} style={{ marginTop: 6, background: "none", border: "none", padding: 0, color: G.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                    {sv ? "Visa →" : "Show →"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {teaser && (
        <div role="status" style={{ position: "fixed", right: 16, bottom: 16, width: "min(340px, calc(100vw - 32px))", background: G.surface, border: `1px solid ${G.gold}55`, borderRadius: 12, padding: "14px 16px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", zIndex: 250 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <Megaphone size={16} color={G.gold} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: G.gold, fontWeight: 600, marginBottom: 2 }}>{sv ? "Nytt i tRAT" : "What's new"}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: G.text, marginBottom: 4 }}>{text(teaser.title)}</div>
              <div style={{ fontSize: 13, color: G.sub, lineHeight: 1.5 }}>{text(teaser.body)}</div>
              <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
                <button onClick={() => { setTeaserClosed(true); openPanel(); }} style={{ background: "none", border: "none", padding: 0, color: G.gold, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                  {sv ? "Se allt nytt" : "See what's new"}
                </button>
              </div>
            </div>
            <button onClick={() => { setTeaserClosed(true); markAllRead(); }} aria-label={sv ? "Stäng" : "Close"} style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", padding: 2, lineHeight: 0 }}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { WhatsNew };
